<template>
  <div data-id-e2e="activeWorkpackages">
    <WorkpackagesToolbar
      v-if="showToolbar"
      doc-path="020-workpackage"
      :entity="entities.workpackage"
      @on-search="onSearch"
      @on-sort="onSort"
    />

    <h2 v-if="!showToolbar && activeWorkpackages.length" class="workpackages__title">
      {{ $t('workpackages.title') }} <DocsLink link="toolguide/020-workpackage.html" />
    </h2>

    <div>
      <AddWorkpackageButtons class="mt-4" />

      <v-row>
        <v-col class="horizontal-divider-container">
          <div class="horizontal-divider" />
        </v-col>
      </v-row>
    </div>

    <v-card class="col-box">
      <v-alert
        v-if="loading"
        border="start"
        border-color="primary"
        color="primary-accent-4"
        variant="tonal"
      >
        <v-progress-circular indeterminate color="primary" size="20" />
        {{ $t('workpackagePage.loadingActiveWorkpackages') }}
      </v-alert>
      <v-row no-gutters>
        <v-col class="workpackages__container colspan">
          <WorkpackagesList
            v-if="sortedWorkpackagesList.length"
            :entity="entities.workpackage"
            :is-link="isLink"
            :visible-workpackages="visibleWorkpackages"
            :workpackages="sortedWorkpackagesList"
          />
          <v-alert v-if="!loading && !visibleWorkpackages.length" type="info" variant="tonal">
            {{ $t('workpackages.noActiveWps') }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card>

    <v-row>
      <v-col class="horizontal-divider-container">
        <div class="horizontal-divider" />
      </v-col>
    </v-row>

    <AddWorkpackageButtons class="mb-6" />
  </div>
</template>

<script>
import { filter, get, includes, map } from 'lodash';
import { mapActions, mapState } from 'pinia';
import jobapi from '@enums/jobapi';
import entities from '@enums/entities';
import sortUtils from '@/utils/sort-utils';
import searchUtils from '@/utils/search-utils';

// Stores
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useContextStore } from '@/stores/context';
import { useBundlesStore } from '@/stores/bundles';

export default {
  props: {
    isLink: {
      type: Boolean,
      required: false,
      default: false,
    },

    showToolbar: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      sortOrder: null,
      searchText: '',
      entities,
    };
  },

  computed: {
    ...mapState(useBundlesStore, ['selectedBundle']),
    ...mapState(useWorkpackagesStore, ['loading', 'activeWorkpackages']),
    ...mapState(useContextStore, ['getClientConfig']),

    visibleWorkpackages() {
      return searchUtils.getResultsByNameDescription(
        this.searchText,
        this.activeWorkpackages,
        this.sortOrder
      );
    },

    sortedWorkpackagesList() {
      return this.sortOrder
        ? sortUtils.sortItemList(this.activeWorkpackages, this.sortOrder)
        : this.activeWorkpackages;
    },

    bundleId() {
      return get(this.selectedBundle, '_id');
    },
  },

  async created() {
    // clear workpackage state to avoid displaying incorrect workpackages in list while new
    // workpackages are loading
    this.setWorkpackages([]);
    // initialise getting all workpackages
    await this.fetchWorkpackages({ bundleId: this.bundleId });
    // Fetch all templates including archived so that WPs created from an archived template
    // can still resolve and display the template name.
    await this.fetchWorkpackages({ shouldGetTemplates: true, ignoreArchivedFlag: true });
  },

  methods: {
    ...mapActions(useWorkpackagesStore, [
      'fetchWorkpackages',
      'setWorkpackageSetupExpanded',
      'setWorkpackages',
    ]),

    getWorkpackagesWithActiveJob(jobName) {
      return map(
        filter(this.activeWorkpackages, function (wp) {
          return (
            wp.jobs &&
            wp.jobs[jobName] &&
            !includes(jobapi.jobFinishedStatuses, wp.jobs[jobName].status)
          );
        }),
        wp => wp._id
      );
    },

    onSearch(searchText) {
      this.loadingActiveWorkpackages = true;
      this.searchText = searchText;
      if (!searchText) {
        this.loadingActiveWorkpackages = false;
        return;
      }
      this.loadingActiveWorkpackages = false;
    },

    onSort(sortOrder) {
      this.sortOrder = sortOrder;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.workpackages {
  &__container {
    overflow-x: auto;
  }

  &__title {
    display: block;
    font-size: 1.2rem;
    font-weight: bold;
    margin: 0 0 10px 38px;
  }
}

:deep(.v-application .headline) {
  font-family: $assortment-regular-font;
}

:deep(.progress-bar) {
  padding-bottom: 10em;
}
</style>
